/**
 * Post-login destination handling, shared by both trees. Email deep links land on a gated
 * route first, so the destination has to survive the bounce through the login page.
 * The parent tree's named wrappers live in `parent-links.helpers.ts`; the staff tree's are here.
 */
import { HOME_ROUTE, LOGIN_ROUTE, PARENT_HOME_ROUTE } from '../../../_configuration';

export const NEXT_PARAM = 'next';

interface INextPathGuards {
  loginRoute: string;
  requirePrefix?: string;
  forbidPrefix?: string;
}

// Placeholder origin: `next` is parsed as the browser would read it, then only its path and query are kept.
const PATH_BASE = 'https://placeholder.invalid';

// Discards any origin, protocol, or tab/backslash trick, so the result is always a path on this site.
const toSitePath = (next: string): string | null => {
  try {
    const { pathname, search } = new URL(next, PATH_BASE);
    return `/${pathname.replace(/^\/+/, '')}${search}`;
  } catch (error) {
    console.warn('Ignoring an unparseable next path:', error);
    return null;
  }
};

// The same cleanup as the reader, so `next` only ever carries a path and query; unparseable, it is left off.
export const loginPathWithNext = (loginRoute: string, pathname: string, search?: string) => {
  const path = toSitePath(`${pathname}${search || ''}`);
  return path ? `${loginRoute}?${NEXT_PARAM}=${encodeURIComponent(path)}` : loginRoute;
};

// A path in the other tree is refused, and bouncing back to the login page is a loop.
export const readNextPath = (search: string, guards: INextPathGuards): string | null => {
  const next = new URLSearchParams(search).get(NEXT_PARAM);
  const path = next ? toSitePath(next) : null;
  if (!path) return null;
  if (guards.requirePrefix && !path.startsWith(guards.requirePrefix)) return null;
  if (guards.forbidPrefix && path.startsWith(guards.forbidPrefix)) return null;
  return path === guards.loginRoute ? null : path;
};

export const staffLoginPathWithNext = (pathname: string, search?: string) =>
  loginPathWithNext(LOGIN_ROUTE, pathname, search);

// Staff and parent are separate sessions, so a staff login never lands in the parent tree.
export const readStaffNextPath = (search: string): string | null =>
  readNextPath(search, { loginRoute: LOGIN_ROUTE, forbidPrefix: PARENT_HOME_ROUTE });

// Where a signed-in staff user goes from the login page: the deep link they were headed to, else the dashboard.
export const staffPostLoginPath = (search: string): string => readStaffNextPath(search) || HOME_ROUTE;
